import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { AlertTriangle, ChevronRight } from "lucide-react";
import { api } from "../../api";
import { TRAINER } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { GymLoader } from "../../components/common/GymLoader";

// UX-TR-01 §1 SCR-P06-20: "My Clients" — server-derived assigned-clients-only list (§0.1). No
// search/filter over the ORG's full member directory exists here on purpose — a trainer can
// only ever see who they've actually booked or been assigned.
export default function Clients() {
  const reducedMotion = useReducedMotion();
  const navigate = useNavigate();
  const clientsQ = useQuery({ queryKey: ["trainer-clients"], queryFn: async () => (await api.get("/trainer/clients")).data });

  if (clientsQ.isLoading) return <GymLoader fullPage label="Loading clients…" testId="trainer-clients-loader" />;
  const clients = clientsQ.data?.clients || [];

  return (
    <div data-testid={TRAINER.clientsPage} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>My clients</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Everyone who's booked a session or been assigned a program with you.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="rounded-[14px] overflow-hidden" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        {clients.length === 0 && <p className="text-sm px-4 py-6" style={{ color: "#68686E" }}>No clients yet.</p>}
        {clients.map((c) => (
          <button
            key={c.id}
            data-testid={TRAINER.clientRow(c.id)}
            onClick={() => navigate(`/trainer/clients/${c.id}`)}
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#FAFAF8] transition-colors"
            style={{ borderBottom: "1px solid #F4F3EF" }}
          >
            <div>
              <p className="text-sm font-medium" style={{ color: "#141416" }}>{c.name}</p>
              <p className="text-xs mt-0.5" style={{ color: "#68686E" }}>{c.phone}</p>
            </div>
            <div className="flex items-center gap-2">
              {c.slipping && (
                <span data-testid={TRAINER.clientSlippingBadge(c.id)} className="status-pill overdue"><AlertTriangle size={11} className="mr-0.5" /> Slipping</span>
              )}
              <ChevronRight size={16} style={{ color: "#68686E" }} />
            </div>
          </button>
        ))}
      </motion.div>
    </div>
  );
}
